/* Calidez Moda — bundle picker and delivery estimate. */
(() => {
  const formatMoney = (cents, format) => {
    const value = (Number(cents) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withSep = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const replacements = {
      amount: `${withSep(',')}.${dec}`,
      amount_no_decimals: withSep(','),
      amount_with_comma_separator: `${withSep('.')},${dec}`,
      amount_no_decimals_with_comma_separator: withSep('.'),
      amount_with_apostrophe_separator: `${withSep("'")}.${dec}`,
    };
    return (format || '{{amount_with_comma_separator}} €').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => replacements[key] ?? replacements.amount);
  };

  class CmBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.data = JSON.parse(this.querySelector('[data-bundle-json]').textContent);
      this.variants = this.data.variants;
      this.form = document.getElementById(this.dataset.formId);
      this.tiers = [...this.querySelectorAll('.cm-tier')];
      this.itemsEl = this.querySelector('[data-items]');
      // Free gifts unlocked by the pack (e.g. one pillowcase per pillow in the 2 and 4 packs), added to the cart at 0 €.
      this.gift = this.data.gift || null;
      this.giftsEl = this.querySelector('[data-gift-items]');
      this.gifts = [];
      // Layouts: "tiles" (quantity tiles + one card per unit), "cards" (stacked packs, pickers inside the chosen pack),
      // "slots" (one slot per unit, a shared colour row that fills the active slot, one size for all).
      this.layout = this.dataset.layout || 'tiles';
      this.activeSlot = 0;
      this.template =
        (this.layout === 'cards' && this.querySelector('[data-item-template-row]')) ||
        (this.layout === 'compact' && this.querySelector('[data-item-template-compact]')) ||
        this.querySelector('[data-item-template]');
      this.moreBtn = this.querySelector('[data-more]');

      const current = this.variants.find((v) => v.id === this.data.current) || this.variants[0];
      this.items = [current];

      this.tiers.forEach((tier) => tier.addEventListener('click', () => this.setQty(Number(tier.dataset.qty))));
      this.moreBtn.addEventListener('click', () => {
        const next = this.nextTier();
        if (next) this.setQty(Number(next.dataset.qty));
      });

      this.closest('.product__info-container')?.classList.add('cm-has-bundle');
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('submit', this.onSubmit, true);
      this.onStickyClick = this.onStickyClick.bind(this);
      document.addEventListener('click', this.onStickyClick, true);

      const preselected = this.tiers.find((t) => t.dataset.index === this.dataset.preselected) || this.tiers[0];
      this.setQty(Number(preselected?.dataset.qty || 1));
      this.watchAtc();
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('click', this.onStickyClick, true);
      this.atcObserver?.disconnect();
    }

    get qty() {
      return this.items.length;
    }

    activeTier() {
      return this.tiers.find((t) => Number(t.dataset.qty) === this.qty);
    }

    nextTier() {
      return this.tiers.find((t) => Number(t.dataset.qty) > this.qty);
    }

    money(cents) {
      return formatMoney(cents, this.data.moneyFormat);
    }

    setQty(qty) {
      while (this.items.length < qty) this.items.push(this.items[this.items.length - 1]);
      this.items.length = qty;
      this.syncGifts();
      this.renderItems();
      this.update();
    }

    // Reference ("before") price of one unit: the product's compare-at price when the block is set to use it.
    refPrice(variant) {
      return this.data.useCompare && variant.compare > variant.price ? variant.compare : variant.price;
    }

    totals() {
      const sum = this.items.reduce((acc, v) => acc + v.price, 0);
      const off = Number(this.activeTier()?.dataset.off || 0);
      const giftsValue = this.gift ? this.gifts.length * this.gift.value : 0;
      const full = this.items.reduce((acc, v) => acc + this.refPrice(v), 0) + giftsValue;
      const total = sum - off;
      return { full, total, off, saved: full - total };
    }

    update() {
      this.tiers.forEach((tier) => {
        const active = Number(tier.dataset.qty) === this.qty;
        tier.setAttribute('aria-checked', active ? 'true' : 'false');
        tier.classList.toggle('is-active', active);
        const q = Number(tier.dataset.qty);
        const tierTotal = this.items[0].price * q - Number(tier.dataset.off || 0);
        const tierGifts = this.gift ? Number(this.gift.perTier[String(q)] || 0) * this.gift.value : 0;
        const tierRef = this.refPrice(this.items[0]) * q + tierGifts;
        const tierSaved = tierRef - tierTotal;
        const unitEl = tier.querySelector('[data-tier-unit]');
        if (unitEl) unitEl.textContent = this.money(Math.round(tierTotal / q));
        const unitWrap = tier.querySelector('[data-tier-unit-wrap]');
        if (unitWrap) unitWrap.hidden = this.layout !== 'compact' && tierSaved > 0;
        const pctEl = tier.querySelector('[data-tier-pct]');
        if (pctEl) {
          const pct = tierRef > 0 ? Math.round((tierSaved * 100) / tierRef) : 0;
          pctEl.hidden = pct <= 0;
          pctEl.textContent = `-${pct} %`;
        }
        const totalEl = tier.querySelector('[data-tier-total]');
        if (totalEl) totalEl.textContent = this.money(tierTotal);
        const pillEl = tier.querySelector('.cm-tier__pctpill');
        if (pillEl && tierRef > 0) pillEl.textContent = `-${Math.round((tierSaved * 100) / tierRef)} %`;
        const fullEl = tier.querySelector('[data-tier-full]');
        if (fullEl) {
          fullEl.textContent = this.money(tierRef);
          fullEl.hidden = tierSaved <= 0;
        }
        const saveEl = tier.querySelector('[data-tier-save]');
        if (saveEl) {
          saveEl.hidden = tierSaved <= 0;
          const savedEl = saveEl.querySelector('[data-tier-saved]');
          if (savedEl) savedEl.textContent = this.money(tierSaved);
        }
      });

      const { full, total, saved } = this.totals();
      this.querySelector('[data-total]').textContent = this.money(total);
      const compare = this.querySelector('[data-compare]');
      compare.hidden = saved <= 0;
      compare.textContent = saved > 0 ? this.money(full) : '';
      const savedText = this.querySelector('[data-saved-text]');
      if (savedText) {
        const tag = this.dataset.shippingTag || '';
        savedText.innerHTML = saved > 0 ? `${tag}${tag ? ' + ' : ''}ahorras <b>${this.money(saved)}</b>` : tag;
      }

      const next = this.nextTier();
      this.moreBtn.hidden = !next;
      if (next) {
        const q = Number(next.dataset.qty);
        const nextOff = Number(next.dataset.off || 0);
        const unit = Math.round((this.items[0].price * q - nextOff) / q);
        const unitWord = this.data.unitSingular;
        const mark = this.data.feminine ? 'ª' : 'º';
        const article = this.data.feminine ? 'una' : 'un';
        this.querySelector('[data-more-title]').textContent = `Añade ${article} ${q}${mark} ${unitWord}`;
        let pct = nextOff > 0 ? Math.round((nextOff * 100) / (this.items[0].price * q)) : 0;
        if (this.layout === 'compact') {
          // Same reference as the tiles, so the % matches the one shown on the pack.
          const ref = this.refPrice(this.items[0]) * q;
          const nextTotal = this.items[0].price * q - nextOff;
          pct = ref > nextTotal ? Math.round(((ref - nextTotal) * 100) / ref) : 0;
          this.querySelector('[data-more-sub]').textContent = `Ahora ${this.money(unit)} / ${unitWord}`;
        } else {
          this.querySelector('[data-more-sub]').textContent = `Ahora ${this.money(unit)} / ${unitWord}${nextOff > 0 ? ` · Ahorras ${this.money(nextOff)}` : ''}`;
        }
        this.querySelector('[data-more-badge]').textContent = pct ? `-${pct}%` : '';
      }

      this.updateAtc();
    }

    renderItems() {
      if (this.layout === 'slots') {
        this.renderSlots();
        return;
      }
      if (this.layout === 'cards') {
        const slot = this.activeTier()?.closest('.cm-card')?.querySelector('[data-card-items]');
        if (slot && this.itemsEl.parentElement !== slot) slot.appendChild(this.itemsEl);
      }
      this.renderGifts();
      this.itemsEl.innerHTML = '';
      this.items.forEach((variant, index) => {
        const node = this.template.content.firstElementChild.cloneNode(true);
        node.querySelector('[data-item-name]').textContent = `${this.data.itemLabel} ${index + 1}`;
        this.fillItem(node, variant, index);
        const remove = node.querySelector('[data-item-remove]');
        if (remove) {
          remove.hidden = this.qty < 2;
          remove.addEventListener('click', () => this.removeItem(index));
        }
        this.itemsEl.appendChild(node);
      });
    }

    fillItem(node, variant, index) {
      const img = node.querySelector('[data-item-img]');
      if (img) {
        img.src = variant.image;
        img.alt = variant.options.join(' / ');
      }
      node.querySelector('[data-item-title]').textContent =
        this.layout === 'cards' && this.data.colorIndex >= 0 ? variant.options[this.data.colorIndex] : variant.options.join(' · ');

      const colorsEl = node.querySelector('[data-item-colors]');
      const selectsEl = node.querySelector('[data-item-selects]');
      colorsEl.innerHTML = '';
      selectsEl.innerHTML = '';

      this.data.options.forEach((option, optIndex) => {
        if (optIndex === this.data.colorIndex && this.layout !== 'compact') {
          option.values.forEach((value) => {
            const match = this.findVariant(variant, optIndex, value);
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'cm-swatch';
            btn.setAttribute('role', 'radio');
            btn.setAttribute('aria-checked', variant.options[optIndex] === value ? 'true' : 'false');
            btn.setAttribute('aria-label', `${option.name}: ${value}`);
            btn.title = value;
            if (!match || !match.available) btn.classList.add('is-unavailable');
            btn.innerHTML = `<img src="${match ? match.image : variant.image}" alt="" width="80" height="80" loading="lazy">`;
            btn.addEventListener('click', () => this.changeItem(index, optIndex, value));
            colorsEl.appendChild(btn);
          });
          return;
        }
        const label = document.createElement('label');
        label.className = 'cm-select';
        const id = `cm-${this.dataset.section}-${index}-${optIndex}`;
        label.setAttribute('for', id);
        label.innerHTML = `<span class="cm-select__label">${option.name}</span>`;
        const select = document.createElement('select');
        select.id = id;
        option.values.forEach((value) => {
          const match = this.findVariant(variant, optIndex, value);
          const opt = new Option(value, value, false, variant.options[optIndex] === value);
          if (!match || !match.available) opt.textContent = `${value} — agotado`;
          select.add(opt);
        });
        select.addEventListener('change', () => this.changeItem(index, optIndex, select.value));
        label.appendChild(select);
        selectsEl.appendChild(label);
      });
      // The free gift of this pillow (same size as the pillow): only the remaining choice (e.g. girl / boy) is shown.
      if (this.gift && index < this.gifts.length) {
        const gift = this.gifts[index];
        const sizeIdx = this.giftSizeIndex();
        const giftVariant = this.giftVariant(gift);
        const wrap = document.createElement('label');
        wrap.className = 'cm-select cm-select--gift';
        wrap.innerHTML = `<img src="${giftVariant?.image || this.gift.image}" alt="" width="52" height="52" loading="lazy"><span class="cm-select__label">Funda</span>`;
        this.gift.options.forEach((option, k) => {
          if (k === sizeIdx) return;
          const select = document.createElement('select');
          select.setAttribute('aria-label', `${option.name} · ${this.gift.label} ${index + 1}`);
          option.values.forEach((value) => select.add(new Option(value, value, false, gift.options[k] === value)));
          select.addEventListener('change', () => {
            gift.options[k] = select.value;
            const v = this.giftVariant(gift);
            const img = wrap.querySelector('img');
            if (img && v?.image) img.src = v.image;
          });
          wrap.appendChild(select);
        });
        selectsEl.appendChild(wrap);
      }
    }

    findVariant(base, optIndex, value) {
      const wanted = base.options.slice();
      wanted[optIndex] = value;
      return (
        this.variants.find((v) => v.options.every((o, i) => o === wanted[i])) ||
        this.variants.find((v) => v.options[optIndex] === value && v.available)
      );
    }

    changeItem(index, optIndex, value) {
      const variant = this.findVariant(this.items[index], optIndex, value);
      if (!variant) return;
      this.items[index] = variant;
      if (this.layout === 'slots') this.renderSlots();
      else this.fillItem(this.itemsEl.children[index], variant, index);
      if (this.gift) {
        this.syncGifts();
        this.renderGifts();
      }
      if (index === 0) this.syncNativePicker(variant);
      this.update();
    }

    giftSizeIndex() {
      return this.gift ? this.gift.options.findIndex((o) => /talla|edad|size|taille/i.test(o.name)) : -1;
    }

    // Keep one gift per unlocked slot; each gift follows the size of "its" pillow until the customer changes it.
    syncGifts() {
      if (!this.gift) return;
      const count = Number(this.gift.perTier[String(this.qty)] || 0);
      const sizeIdx = this.giftSizeIndex();
      while (this.gifts.length < count) {
        this.gifts.push({ options: this.gift.options.map((o) => o.values[0]), manual: false });
      }
      this.gifts.length = count;
      this.gifts.forEach((gift, i) => {
        if (sizeIdx < 0) return;
        const pillow = this.items[i] || this.items[this.items.length - 1];
        const size = pillow.options.find((v) => this.gift.options[sizeIdx].values.includes(v));
        if (size) gift.options[sizeIdx] = size;
      });
    }

    giftVariant(gift) {
      return this.gift.variants.find((v) => v.options.every((o, k) => o === gift.options[k]));
    }

    renderGifts() {
      if (this.giftsEl) this.giftsEl.innerHTML = '';
    }

    removeItem(index) {
      if (this.qty < 2) return;
      this.items.splice(index, 1);
      if (index === 0) this.syncNativePicker(this.items[0]);
      this.renderItems();
      this.update();
    }

    renderSlots() {
      const colorIdx = this.data.colorIndex;
      const sizeIdx = this.data.options.findIndex((o, i) => i !== colorIdx);
      if (this.activeSlot >= this.qty) this.activeSlot = this.qty - 1;
      const active = this.items[this.activeSlot];
      const el = this.itemsEl;
      el.innerHTML = '';
      const make = (tag, cls, text) => {
        const node = document.createElement(tag);
        if (cls) node.className = cls;
        if (text) node.textContent = text;
        return node;
      };
      const swatchImg = (src) => `<img src="${src}" alt="" width="80" height="80" loading="lazy">`;

      if (this.qty > 1) {
        el.appendChild(make('p', 'cm-slots__label', `Tus ${this.data.unitPlural}`));
        const slots = make('div', 'cm-slots');
        this.items.forEach((variant, i) => {
          const btn = make('button', 'cm-slot');
          btn.type = 'button';
          btn.setAttribute('aria-pressed', i === this.activeSlot ? 'true' : 'false');
          btn.innerHTML = `<span class="cm-slot__img">${swatchImg(variant.image)}</span><span class="cm-slot__text"><b>${i + 1}</b> ${colorIdx >= 0 ? variant.options[colorIdx] : ''}</span>`;
          btn.addEventListener('click', () => {
            this.activeSlot = i;
            this.renderSlots();
          });
          slots.appendChild(btn);
        });
        el.appendChild(slots);
      }

      if (colorIdx >= 0) {
        const option = this.data.options[colorIdx];
        const label = make('p', 'cm-slots__label');
        label.innerHTML = `${option.name}${this.qty > 1 ? ` · ${this.data.itemLabel} ${this.activeSlot + 1}` : ''}: <b>${active.options[colorIdx]}</b>`;
        el.appendChild(label);
        const row = make('div', 'cm-item__colors');
        row.setAttribute('role', 'radiogroup');
        option.values.forEach((value) => {
          const match = this.findVariant(active, colorIdx, value);
          const btn = make('button', 'cm-swatch');
          btn.type = 'button';
          btn.setAttribute('role', 'radio');
          btn.setAttribute('aria-checked', active.options[colorIdx] === value ? 'true' : 'false');
          btn.setAttribute('aria-label', `${option.name}: ${value}`);
          btn.title = value;
          if (!match || !match.available) btn.classList.add('is-unavailable');
          btn.innerHTML = swatchImg(match ? match.image : active.image);
          btn.addEventListener('click', () => {
            const slot = this.activeSlot;
            const variant = this.findVariant(this.items[slot], colorIdx, value);
            if (!variant) return;
            this.items[slot] = variant;
            if (slot === 0) this.syncNativePicker(variant);
            // Move on to the next unit so a pack is filled in a few taps.
            if (slot < this.qty - 1) this.activeSlot = slot + 1;
            this.renderSlots();
            this.update();
          });
          row.appendChild(btn);
        });
        el.appendChild(row);
      }

      if (sizeIdx >= 0) {
        const option = this.data.options[sizeIdx];
        el.appendChild(make('p', 'cm-slots__label', this.qty > 1 ? `${option.name} · la misma para todas` : option.name));
        const chips = make('div', 'cm-chips');
        option.values.forEach((value) => {
          const btn = make('button', 'cm-chip', value);
          btn.type = 'button';
          btn.setAttribute('aria-pressed', this.items[0].options[sizeIdx] === value ? 'true' : 'false');
          if (!this.findVariant(this.items[0], sizeIdx, value)?.available) btn.classList.add('is-unavailable');
          btn.addEventListener('click', () => {
            this.items = this.items.map((v) => this.findVariant(v, sizeIdx, value) || v);
            this.syncNativePicker(this.items[0]);
            this.renderSlots();
            this.update();
          });
          chips.appendChild(btn);
        });
        el.appendChild(chips);
      }
    }

    syncNativePicker(variant) {
      const scope = this.closest('.product__info-container') || document;
      variant.options.forEach((value, i) => {
        const name = this.data.options[i].name;
        const radio = [...scope.querySelectorAll('variant-selects input[type="radio"], variant-radios input[type="radio"]')].find(
          (input) => input.value === value && input.name.startsWith(name)
        );
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        const select = [...scope.querySelectorAll('variant-selects select, variant-radios select')].find((s) => s.name.includes(name));
        if (select && select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      const idInput = this.form?.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;
    }

    watchAtc() {
      this.atc = this.form?.querySelector('[name="add"]');
      if (!this.atc) return;
      this.atcObserver = new MutationObserver(() => {
        if (!this.atc.querySelector('.cm-atc-price')) this.updateAtc();
      });
      this.atcObserver.observe(this.atc, { childList: true, subtree: true });
      this.updateAtc();
    }

    updateAtc() {
      if (!this.atc || this.atc.disabled) return;
      const label = this.atc.querySelector('.main-atc__label__text') || this.atc.querySelector('span') || this.atc;
      let price = this.atc.querySelector('.cm-atc-price');
      if (!price) {
        price = document.createElement('span');
        price.className = 'cm-atc-price';
        label.after(price);
      }
      const { full, total, saved } = this.totals();
      const text = saved > 0 ? `${this.money(total)} <s>${this.money(full)}</s>` : this.money(total);
      if (price.innerHTML !== text) price.innerHTML = text;
    }

    // The sticky bar has its own add-to-cart button; route it through the main form so the pack is respected.
    onStickyClick(event) {
      const btn = event.target.closest('sticky-atc button');
      if (!btn || !this.form || btn.closest('form') === this.form || btn.getAttribute('form') !== this.form.getAttribute('id')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const main = this.form.querySelector('[type="submit"][name="add"]') || this.form.querySelector('[type="submit"]');
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(main || undefined);
      else main?.click();
    }

    async onSubmit(event) {
      if (event.target !== this.form) return;
      // Remember which page version led to the order (hidden cart attribute, visible on the order in the admin).
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _version_pagina: this.dataset.version } }),
      }).catch(() => {});
      const idInput = this.form.querySelector('input[name="id"]');
      if (this.qty < 2 && !this.gifts.length) {
        if (idInput) idInput.value = this.items[0].id;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();

      const grouped = new Map();
      this.items.forEach((v) => grouped.set(v.id, (grouped.get(v.id) || 0) + 1));
      const items = [...grouped].map(([id, quantity]) => ({ id, quantity }));
      if (this.gift) {
        this.gifts.forEach((gift) => {
          const variant = this.giftVariant(gift);
          if (variant) items.push({ id: variant.id, quantity: 1, properties: { _regalo: `Pack de ${this.qty}` } });
        });
      }

      const cartDrawer = document.querySelector('cart-drawer');
      let sections = [];
      try {
        sections = cartDrawer?.getSectionsToRender ? cartDrawer.getSectionsToRender().map((s) => s.id) : [];
      } catch (e) {
        sections = [];
      }

      this.atc?.classList.add('loading');
      this.atc?.setAttribute('aria-disabled', 'true');
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || 'add failed');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && json.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(json);
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (e) {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } finally {
        this.atc?.classList.remove('loading');
        this.atc?.removeAttribute('aria-disabled');
      }
    }
  }

  class CmDelivery extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min || 5);
      const max = Number(this.dataset.max || 7);
      const from = new Date();
      const to = new Date();
      from.setDate(from.getDate() + min);
      to.setDate(to.getDate() + max);
      const day = (d) => d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' });
      const month = (d) => d.toLocaleDateString('es-ES', { month: 'long' });
      const fromText = from.getMonth() === to.getMonth() ? day(from) : `${day(from)} de ${month(from)}`;
      const toText = `${day(to)} de ${month(to)}`;
      const line = this.querySelector('[data-line]');
      if (line) line.innerHTML = `${this.dataset.verb || 'Recíbelas'} entre el <b>${fromText.replace(',', '')}</b> y el <b>${toText.replace(',', '')}</b>`;
    }
  }

  // Colour cards: pick the colour in the bundle instead of reloading the page.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cm-color]');
    const bundle = document.querySelector('cm-bundle');
    if (!link || !bundle || !bundle.items) return;
    event.preventDefault();
    bundle.changeItem(0, Number(link.dataset.cmOption), link.dataset.cmColor);
    bundle.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  // Scroll reveal for the custom sections.
  const revealTargets = '.cm-section .cm-head, .cm-feature, .cm-video, .cm-color, .cm-guarantee__item, .cm-guarantee__head, .cm-split__media, .cm-split__body, .cm-banner__box, .cm-faq__list';
  const initReveal = () => {
    if (!document.querySelector('[data-cm-reveal]')) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    document.querySelectorAll(revealTargets).forEach((el) => {
      const siblings = el.parentElement ? [...el.parentElement.children].filter((c) => c.matches(revealTargets)) : [];
      const index = siblings.indexOf(el);
      if (index > 0) el.style.setProperty('--cm-delay', `${Math.min(index, 4) * 0.08}s`);
      el.classList.add('cm-reveal');
      io.observe(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReveal);
  else initReveal();

  // Buttons that should bring the shopper back to the buy box.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-cm-scroll-buy]');
    const bundle = document.querySelector('cm-bundle');
    if (!btn || !bundle || (btn.getAttribute('href') && btn.getAttribute('href') !== '#MainContent')) return;
    event.preventDefault();
    bundle.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  // Reviews carousel: first page comes from the Trustoo metafield, more are fetched from Trustoo's public API.
  class CmReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.nav = this.querySelector('.cm-reviews__nav');
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
      this.updateNav();
      window.addEventListener('resize', () => this.updateNav());
      this.loadMore();
    }

    step(direction) {
      const card = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      const width = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
      this.track.scrollBy({ left: direction * width, behavior: 'smooth' });
    }

    updateNav() {
      if (this.nav) this.nav.hidden = this.track.scrollWidth <= this.track.clientWidth + 4;
    }

    async loadMore() {
      const { shop, product, limit } = this.dataset;
      if (!shop || !product) return;
      try {
        const url = `https://api.trustoo.io/api/v1/reviews/get_product_reviews?shop_id=${shop}&product_id=${product}&limit=${limit || 12}&page=1&sort_by=commented-at-descending`;
        const json = await fetch(url).then((r) => r.json());
        const list = json?.data?.list || [];
        if (list.length <= this.track.children.length) return;
        const template = this.querySelector('[data-card-template]');
        this.track.innerHTML = '';
        list.forEach((review) => this.track.appendChild(this.fillCard(template.content.firstElementChild.cloneNode(true), review)));
        this.updateNav();
      } catch (e) {
        // Keep the server-rendered reviews.
      }
    }

    fillCard(card, review) {
      const star = Number(review.star) || 0;
      const stars = card.querySelector('.cm-stars');
      stars?.style.setProperty('--cm-stars', `${Math.min(star, 5) * 20}%`);
      stars?.setAttribute('aria-label', `${star} de 5 estrellas`);
      const date = (review.commented_at || '').slice(0, 10).split('-');
      card.querySelector('[data-date]').textContent = date.length === 3 ? `${date[2]}/${date[1]}/${date[0]}` : '';
      const title = card.querySelector('[data-title]');
      title.textContent = review.title || '';
      title.hidden = !review.title;
      card.querySelector('[data-text]').textContent = review.content || '';
      card.querySelector('[data-author]').textContent = (review.author || '').trim();
      card.querySelector('[data-verified]').hidden = !review.verified_by_shop;
      return card;
    }
  }
  if (!customElements.get('cm-reviews')) customElements.define('cm-reviews', CmReviews);

  // Videos: download and play only when they come into view, pause when they leave.
  const initLazyVideos = () => {
    const videos = document.querySelectorAll('video[data-cm-lazy-video]');
    if (!videos.length) return;
    if (!('IntersectionObserver' in window)) {
      videos.forEach((v) => { if (v.dataset.poster) v.poster = v.dataset.poster; v.preload = 'auto'; v.play().catch(() => {}); });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target: v, isIntersecting }) => {
        if (isIntersecting) {
          if (v.dataset.poster && !v.poster) v.poster = v.dataset.poster;
          if (v.preload !== 'auto') { v.preload = 'auto'; v.load(); }
          v.play().catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { rootMargin: '400px 0px', threshold: 0.01 });
    videos.forEach((v) => io.observe(v));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initLazyVideos);
  else initLazyVideos();

  if (!customElements.get('cm-bundle')) customElements.define('cm-bundle', CmBundle);

  // Free gift guard: the 0 € pillowcases only stay in the cart while enough pillows are in it (2 → 2 gifts, 4 → 4).
  const CM_GIFT_GUARD = { gift: 'funda-de-regalo', base: 'almohada-ergonomica-infantil', tiers: [[4, 4], [2, 2]] };
  let cmGuardBusy = false;
  async function cmGuardGifts() {
    if (cmGuardBusy) return;
    cmGuardBusy = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await window.fetch(`${root}cart.js`).then((r) => r.json());
      const base = cart.items.filter((i) => i.handle === CM_GIFT_GUARD.base).reduce((a, i) => a + i.quantity, 0);
      const giftLines = cart.items.filter((i) => i.handle === CM_GIFT_GUARD.gift);
      const gifts = giftLines.reduce((a, i) => a + i.quantity, 0);
      const allowed = (CM_GIFT_GUARD.tiers.find(([min]) => base >= min) || [0, 0])[1];
      if (gifts <= allowed) return;
      let excess = gifts - allowed;
      for (const line of giftLines.reverse()) {
        if (excess <= 0) break;
        const quantity = Math.max(0, line.quantity - excess);
        excess -= line.quantity - quantity;
        await window.fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity }),
        });
      }
      window.location.reload();
    } catch (e) {
      // ignore: the guard runs again on the next cart change
    } finally {
      cmGuardBusy = false;
    }
  }
  const cmNativeFetch = window.fetch;
  window.fetch = function (...args) {
    const request = cmNativeFetch.apply(this, args);
    try {
      const url = String(args[0]?.url || args[0] || '');
      if (/\/cart\/(change|update|clear)/.test(url)) {
        request.then(() => {
          clearTimeout(window.__cmGuardTimer);
          window.__cmGuardTimer = setTimeout(cmGuardGifts, 700);
        });
      }
    } catch (e) {
      // never block the original request
    }
    return request;
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cmGuardGifts);
  else cmGuardGifts();
  if (!customElements.get('cm-delivery')) customElements.define('cm-delivery', CmDelivery);
})();
